import { useCallback, useEffect, useState } from "react";
import {
  Users,
  UserPlus,
  Link2,
  ShieldOff,
  RefreshCw,
  KeyRound,
  Smartphone,
  Archive,
  LockOpen,
  Mail,
} from "lucide-react";
import { api } from "@/lib/api";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "read_only", label: "Read Only" },
];

function formatDate(value) {
  if (!value) return "Unknown";
  return new Date(value).toLocaleString("en-GB");
}

function roleLabel(role) {
  if (role === "super_admin") return "Super Admin";
  return ROLES.find((item) => item.value === role)?.label || role;
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState("current");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [creating, setCreating] = useState(false);

  const [message, setMessage] = useState("");
  const [actionLink, setActionLink] = useState("");
  const [actionLinkTitle, setActionLinkTitle] = useState("");

  const clearLink = () => {
    setActionLink("");
    setActionLinkTitle("");
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (error) {
      setUsers([]);
      setMessage(
        error?.response?.data?.detail || "Unable to load users."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const createUser = async (event) => {
    event.preventDefault();
    setCreating(true);
    setMessage("");
    clearLink();

    try {
      await api.post("/admin/users", {
        name: name.trim(),
        email: email.trim(),
        role,
      });

      setName("");
      setEmail("");
      setRole("admin");
      setMessage("Pending user created.");
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to create user."
      );
    } finally {
      setCreating(false);
    }
  };

  const sendInvitation = async (user) => {
    const action = user.invitation_sent_at ? "Resend" : "Send";

    if (!window.confirm(
      `${action} portal invitation to ${user.email}? Any previous setup link will stop working.`
    )) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      await api.post(`/admin/users/${user.id}/send-invitation`);
      setMessage(`Invitation sent to ${user.email}.`);
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to send invitation."
      );
    }
  };


  const generateSetupLink = async (user) => {
    setMessage("");
    clearLink();

    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/setup-token`
      );

      const link =
        `${window.location.origin}/admin/setup#token=${encodeURIComponent(
          data.setup_token
        )}`;

      setActionLink(link);
      setActionLinkTitle("One-time account setup link");
      setMessage(`Setup link generated for ${user.email}.`);
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to generate setup link."
      );
    }
  };

  const generatePasswordReset = async (user) => {
    if (!window.confirm(`Reset the password for ${user.email}? This will revoke all active sessions.`)) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/password-reset-token`
      );

      const link =
        `${window.location.origin}/admin/password-reset#token=${encodeURIComponent(
          data.reset_token
        )}`;

      setActionLink(link);
      setActionLinkTitle("One-time password reset link");
      setMessage(`Password reset link generated for ${user.email}.`);
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail ||
          "Unable to generate password reset link."
      );
    }
  };

  const generateMfaReset = async (user) => {
    if (!window.confirm(`Reset MFA for ${user.email}? Their existing authenticator and recovery codes will stop working.`)) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/mfa-reset-token`
      );

      const link =
        `${window.location.origin}/admin/mfa-reset#token=${encodeURIComponent(
          data.reset_token
        )}`;

      setActionLink(link);
      setActionLinkTitle("One-time MFA reset link");
      setMessage(`MFA reset link generated for ${user.email}.`);
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail ||
          "Unable to generate MFA reset link."
      );
    }
  };

  const updateRole = async (user, newRole) => {
    if (newRole === user.role) return;

    if (!window.confirm(
      `Change ${user.email} from ${roleLabel(user.role)} to ${roleLabel(newRole)}? Their active sessions will be revoked.`
    )) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      await api.patch(`/admin/users/${user.id}/role`, {
        role: newRole,
      });

      setMessage(`Role updated for ${user.email}.`);
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to change role."
      );
      await loadUsers();
    }
  };

  const updateStatus = async (user, status) => {
    const label = status === "disabled" ? "disable" : "reactivate";

    if (!window.confirm(`Are you sure you want to ${label} ${user.email}?`)) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      await api.patch(`/admin/users/${user.id}/status`, { status });
      setMessage(
        `User ${status === "disabled" ? "disabled" : "reactivated"}.`
      );
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to update user."
      );
    }
  };

  const unlockAccount = async (user) => {
    if (!window.confirm(
      `Unlock ${user.email}? This clears failed sign-in lockouts and unfinished MFA challenges only.`
    )) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/unlock`
      );

      const loginCount = data.login_lockouts_cleared ?? 0;
      const mfaCount = data.mfa_challenges_cleared ?? 0;

      setMessage(
        `${user.email} unlocked. ${loginCount} login lockout(s) and ${mfaCount} MFA challenge(s) cleared.`
      );
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to unlock account."
      );
    }
  };


  const revokeSessions = async (user) => {
    if (!window.confirm(`Revoke all sessions for ${user.email}?`)) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      const { data } = await api.post(
        `/admin/users/${user.id}/revoke-sessions`
      );

      setMessage(
        `${data.revoked_count ?? 0} session(s) revoked for ${user.email}.`
      );

      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to revoke sessions."
      );
    }
  };

  const archiveUser = async (user) => {
    if (!window.confirm(
      `Archive ${user.email}? They will lose portal access and their historical audit records will be retained.`
    )) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      await api.post(`/admin/users/${user.id}/archive`);
      setMessage(`${user.email} archived.`);
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to archive user."
      );
    }
  };

  const restoreUser = async (user) => {
    if (!window.confirm(
      `Restore ${user.email}? The account will return to Pending and must complete a new invitation, password and MFA setup before access is restored.`
    )) {
      return;
    }

    setMessage("");
    clearLink();

    try {
      await api.post(`/admin/users/${user.id}/restore`);
      setMessage(
        `${user.email} restored to Pending. Send a new invitation to complete account setup.`
      );
      await loadUsers();
    } catch (error) {
      setMessage(
        error?.response?.data?.detail || "Unable to restore user."
      );
    }
  };

  const filteredUsers = users.filter((user) => {
    if (userFilter === "all") return true;
    if (userFilter === "current") return user.status !== "archived";
    return user.status === userFilter;
  });

  const userFilterCounts = {
    current: users.filter((user) => user.status !== "archived").length,
    pending: users.filter((user) => user.status === "pending").length,
    disabled: users.filter((user) => user.status === "disabled").length,
    archived: users.filter((user) => user.status === "archived").length,
    all: users.length,
  };

  const copyActionLink = async () => {
    try {
      await navigator.clipboard.writeText(actionLink);
      setMessage("Link copied.");
    } catch {
      setMessage("Unable to copy automatically. Copy the link manually.");
    }
  };

  return (
    <div className="max-w-6xl" data-testid="page-admin-users">
      <div className="flex items-center gap-3">
        <div className="rounded-sm bg-navy p-2.5 text-cyan">
          <Users size={22} />
        </div>

        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">
            Users
          </h1>
          <p className="mt-1 text-sm text-slate">
            Create and manage portal users, access and account recovery.
          </p>
        </div>
      </div>

      <section className="mt-8 rounded-md border border-brandborder bg-white p-6">
        <div className="flex items-center gap-2 font-semibold text-navy">
          <UserPlus size={18} />
          Create user
        </div>

        <form
          onSubmit={createUser}
          className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_180px_auto]"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={120}
            placeholder="Full name"
            className="rounded-sm border border-brandborder px-3 py-2.5 text-sm text-navy"
          />

          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            type="email"
            placeholder="Email address"
            className="rounded-sm border border-brandborder px-3 py-2.5 text-sm text-navy"
          />

          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded-sm border border-brandborder bg-white px-3 py-2.5 text-sm text-navy"
          >
            {ROLES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <button
            type="submit"
            disabled={creating}
            className="rounded-sm bg-navy px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create user"}
          </button>
        </form>

        <p className="mt-3 text-xs text-slate">
          Super Admin access cannot be created from this form.
        </p>
      </section>

      {message && (
        <div className="mt-5 rounded-sm border border-brandborder bg-white px-4 py-3 text-sm text-navy">
          {message}
        </div>
      )}

      {actionLink && (
        <section className="mt-5 rounded-md border border-brandborder bg-white p-5">
          <div className="font-semibold text-navy">
            {actionLinkTitle}
          </div>

          <div className="mt-3 break-all rounded-sm bg-lightbg p-3 text-xs text-slate">
            {actionLink}
          </div>

          <button
            type="button"
            onClick={copyActionLink}
            className="mt-3 inline-flex items-center gap-2 rounded-sm border border-brandborder px-3 py-2 text-sm font-semibold text-navy"
          >
            <Link2 size={15} />
            Copy link
          </button>
        </section>
      )}

      <section className="mt-8 rounded-md border border-brandborder bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="font-semibold text-navy">
            Portal users
          </div>

          <button
            type="button"
            onClick={loadUsers}
            disabled={loading}
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy disabled:opacity-50"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {[
            { value: "current", label: "Current" },
            { value: "pending", label: "Pending" },
            { value: "disabled", label: "Disabled" },
            { value: "archived", label: "Archived" },
            { value: "all", label: "All" },
          ].map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setUserFilter(filter.value)}
              className={`rounded-sm border px-3 py-2 text-sm font-semibold transition-colors ${
                userFilter === filter.value
                  ? "border-navy bg-navy text-white"
                  : "border-brandborder bg-white text-slate hover:border-brandblue hover:text-navy"
              }`}
            >
              {filter.label} ({userFilterCounts[filter.value]})
            </button>
          ))}
        </div>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-sm text-slate">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-sm text-slate">No users found for this filter.</div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className="rounded-sm border border-brandborder bg-lightbg p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-navy">
                        {user.name || user.email}
                      </span>

                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate">
                        {user.status}
                      </span>

                      {user.is_current_user && (
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          You
                        </span>
                      )}
                    </div>

                    <div className="mt-2 text-sm text-slate">
                      {user.email}
                    </div>

                    <div className="mt-3 grid gap-1 text-xs text-slate">
                      <div>Role: {roleLabel(user.role)}</div>
                      <div>
                        MFA: {user.mfa_enabled ? "Enabled" : "Not enabled"}
                      </div>
                      <div>Active sessions: {user.active_sessions}</div>
                      <div>Created: {formatDate(user.created_at)}</div>
                      {user.status === "archived" && user.archived_at && (
                        <div>Archived: {formatDate(user.archived_at)}</div>
                      )}
                      {user.invitation_sent_at && (
                        <div>
                          Invitation sent: {formatDate(user.invitation_sent_at)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex max-w-xl flex-wrap items-center gap-2">
                    {!user.is_current_user &&
                      user.role !== "super_admin" &&
                      user.status !== "archived" && (
                        <select
                          value={user.role}
                          onChange={(e) => updateRole(user, e.target.value)}
                          className="rounded-sm border border-brandborder bg-white px-3 py-2 text-sm text-navy"
                        >
                          {ROLES.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      )}

                    {user.status === "pending" && (
                      <>
                        <button
                          type="button"
                          onClick={() => sendInvitation(user)}
                          className="inline-flex items-center gap-2 rounded-sm bg-navy px-3 py-2 text-sm font-semibold text-white"
                        >
                          <Mail size={15} />
                          {user.invitation_sent_at
                            ? "Resend invitation"
                            : "Send invitation"}
                        </button>

                        <button
                          type="button"
                          onClick={() => generateSetupLink(user)}
                          className="rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                        >
                          Generate setup link
                        </button>
                      </>
                    )}

                    {!user.is_current_user &&
                      user.status === "active" && (
                        <>
                          <button
                            type="button"
                            onClick={() => unlockAccount(user)}
                            className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            <LockOpen size={15} />
                            Unlock account
                          </button>

                          <button
                            type="button"
                            onClick={() => generatePasswordReset(user)}
                            className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            <KeyRound size={15} />
                            Reset password
                          </button>

                          <button
                            type="button"
                            onClick={() => generateMfaReset(user)}
                            className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            <Smartphone size={15} />
                            Reset MFA
                          </button>

                          <button
                            type="button"
                            onClick={() => revokeSessions(user)}
                            className="rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            Revoke sessions
                          </button>

                          <button
                            type="button"
                            onClick={() => updateStatus(user, "disabled")}
                            className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            <ShieldOff size={15} />
                            Disable
                          </button>
                        </>
                      )}

                    {!user.is_current_user &&
                      user.status === "disabled" && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => unlockAccount(user)}
                            className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                          >
                            <LockOpen size={15} />
                            Unlock account
                          </button>

                          <button
                          type="button"
                          onClick={() => updateStatus(user, "active")}
                          className="rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                        >
                            Reactivate
                          </button>
                        </div>
                      )}

                    {!user.is_current_user &&
                      user.role !== "super_admin" &&
                      user.status !== "archived" && (
                        <button
                          type="button"
                          onClick={() => archiveUser(user)}
                          className="inline-flex items-center gap-2 rounded-sm border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700"
                        >
                          <Archive size={15} />
                          Archive
                        </button>
                      )}

                    {!user.is_current_user &&
                      user.role !== "super_admin" &&
                      user.status === "archived" && (
                        <button
                          type="button"
                          onClick={() => restoreUser(user)}
                          className="inline-flex items-center gap-2 rounded-sm border border-brandborder bg-white px-3 py-2 text-sm font-semibold text-navy"
                        >
                          <RefreshCw size={15} />
                          Restore
                        </button>
                      )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
